// ============================================
// SMART ASSISTANT — Inline Data Quality Warnings
// Gated by Smart Tools add-on (window.hasAiPack())
// + Settings toggle: enableSmartAssistant (default ON)
// ============================================

const SA_REGISTRY = {};

// Check if SA is globally enabled (settings gate)
function isEnabled() {
  if (window.AppState?.settings?.enableSmartAssistant === false) return false;
  return true;
}

// Levenshtein distance (standard DP)
// TASK-1334 — comparison-only normalizer for the exact-duplicate test: trim +
// collapse internal whitespace runs, then case-fold. Deliberately does NOT
// touch punctuation — `job-po` / `job-vendor-number` register through here and
// PO-12345 vs PO12345 are genuinely different POs. Never used to WRITE a value.
function _normalizeForCompare(v) {
  return String(v == null ? '' : v).trim().replace(/\s+/g, ' ').toLowerCase();
}

function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

// Title-case a string: "JOHN SMITH" → "John Smith"
function titleCase(str) {
  return str.replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

// Format phone as (XXX) XXX-XXXX
function formatPhone(val) {
  const digits = val.replace(/\D/g, '');
  // Handle 10-digit US numbers
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // Handle 11-digit with leading 1
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return null; // Can't format
}

// Basic email validation
function validateEmail(val) {
  const trimmed = val.trim();
  if (!trimmed.includes('@')) return 'Missing @ symbol';
  const parts = trimmed.split('@');
  if (parts.length !== 2 || !parts[0] || !parts[1]) return 'Invalid email format';
  if (!parts[1].includes('.')) return 'Missing domain (e.g. .com)';
  if (parts[1].endsWith('.')) return 'Domain ends with a dot';
  return null; // Valid
}

// Clean formatting issues (respects skipAllCaps)
function cleanFormat(val, options = {}) {
  let cleaned = val.replace(/\s+/g, ' ').trim();
  // Title-case if ALL CAPS and more than 2 chars (unless skipAllCaps)
  if (!options.skipAllCaps && cleaned.length > 2 && cleaned === cleaned.toUpperCase() && /[A-Z]/.test(cleaned)) {
    cleaned = titleCase(cleaned);
  }
  return cleaned;
}

// Check if a value has formatting issues (respects skipAllCaps)
function hasFormatIssues(val, options = {}) {
  if (/\s{2,}/.test(val)) return true; // extra spaces
  if (val !== val.trim()) return true; // leading/trailing spaces
  if (!options.skipAllCaps && val.length > 2 && val === val.toUpperCase() && /[A-Z]/.test(val)) return true; // ALL CAPS
  return false;
}

// TASK-4834 follow-up — the ONLY cleanup an identifier gets: trim + collapse
// internal whitespace runs. Never touches case or punctuation, so it cannot turn
// "ND" into "Nd", "1HGBH41..." into "1hgbh41...", or "PACCAR MX-13" into
// "Paccar Mx-13". Returns null when there is nothing to fix.
function identifierWhitespaceFix(val) {
  const fixed = String(val == null ? '' : val).replace(/\s+/g, ' ').trim();
  return fixed !== val ? fixed : null;
}

// Run the check for a registered field
function runCheck(fieldId) {
  clearWarning(fieldId);

  const input = document.getElementById(fieldId);
  if (!input) return;

  const val = input.value;
  if (!val || !val.trim()) return;

  const reg = SA_REGISTRY[fieldId];
  if (!reg) return;

  const options = reg.options || {};

  // Phone format check
  if (options.phoneFormat) {
    const trimmed = val.trim();
    const digits = trimmed.replace(/\D/g, '');
    if (digits.length >= 7) {
      const formatted = formatPhone(trimmed);
      if (formatted && formatted !== trimmed) {
        showWarning(fieldId, {
          type: 'format',
          icon: 'auto_fix_high',
          message: `Suggestion: "${formatted}"`,
          suggestion: formatted
        });
      }
    }
    return;
  }

  // Email format check
  if (options.emailFormat) {
    const error = validateEmail(val);
    if (error) {
      showWarning(fieldId, {
        type: 'format',
        icon: 'warning',
        message: error
      });
    }
    return;
  }

  // Format-only fields skip duplicate/fuzzy checks.
  // TASK-4834 — `identifier` wins over `formatOnly`: an identifier never gets
  // the title-case Auto-fix, even if both flags are passed.
  if (options.formatOnly && !options.identifier) {
    if (hasFormatIssues(val, options)) {
      const fixed = cleanFormat(val, options);
      if (fixed !== val) {
        showWarning(fieldId, {
          type: 'format',
          icon: 'auto_fix_high',
          message: `Suggestion: "${fixed}"`,
          suggestion: fixed
        });
      }
    }
    return;
  }

  // Full check: duplicate + fuzzy + format
  const items = reg.getter();
  const excludeIdField = options.excludeIdField;
  const excludeId = excludeIdField ? (document.getElementById(excludeIdField)?.value || null) : null;
  const filtered = excludeId ? items.filter(i => i.id !== excludeId) : items;
  const trimmed = val.trim();
  const lower = trimmed.toLowerCase();

  // 1. Exact duplicate (case-insensitive, whitespace-insensitive)
  // TASK-1334 — the typed value was trimmed but the STORED one wasn't, and TITS
  // job titles carry trailing spaces ("Davidson atlas " on all 12 records). So
  // an identical title missed the duplicate branch, fell through to fuzzy
  // (levenshtein 1), and offered the UNTRIMMED stored value as "did you mean" —
  // clicking Apply wrote the trailing space back into a new record.
  // trim + collapse internal runs ONLY: punctuation and case must stay
  // meaningful (job-po / job-vendor-number treat PO-12345 and PO12345 as
  // different POs), so this normalizer stops here on purpose.
  const dup = filtered.find(i => _normalizeForCompare(i.value) === _normalizeForCompare(trimmed));
  if (dup) {
    showWarning(fieldId, {
      type: 'duplicate',
      icon: 'error',
      message: `"${dup.value}" already exists in ${reg.label}.`
    });
    return;
  }

  // TASK-4834 — `identifier: true` = an ID somebody else assigned (unit number,
  // VIN, plate). Exact-duplicate warning ONLY. The two branches below are for
  // NAMES and corrupt identifiers: fuzzy "Did you mean" offered a DIFFERENT real
  // unit behind Apply (207 of 270 live truck units got one: "104" -> "105"), and
  // the title-case Auto-fix rewrites "KW-12" to "Kw-12". Never re-route an
  // identifier through them.
  //
  // Follow-up (task-smartassist-names-2): identifier mode now also covers
  // addresses (state codes, APT/STE/NE/PO BOX/HWY/CR, ZIPs) and product codes
  // (make/model/engine). The one suggestion it may make is whitespace-only.
  if (options.identifier) {
    const wsFix = identifierWhitespaceFix(val);
    if (wsFix) {
      showWarning(fieldId, {
        type: 'format',
        icon: 'auto_fix_high',
        message: `Suggestion: "${wsFix}"`,
        suggestion: wsFix
      });
    }
    return;
  }

  // 2. Fuzzy similar match
  // `noFuzzy: true` (well names, nicknames, job location) — these keep name
  // cleanup (step 3) but NEVER get a "Did you mean" that swaps in a DIFFERENT
  // record's value: "Atlas 3-1H" -> "Atlas 4-1H" is a different well, and
  // "NW/4 Sec 12" -> "NW/4 Sec 13" a different location. The only match these
  // fields act on is the exact case/whitespace-insensitive one in step 1.
  if (!options.noFuzzy && trimmed.length >= 3) {
    // Tighter threshold for skipAllCaps fields (oil field codes)
    const threshold = options.skipAllCaps
      ? Math.max(1, Math.ceil(trimmed.length * 0.15))
      : Math.max(2, Math.ceil(trimmed.length * 0.2));
    let bestMatch = null;
    let bestDist = Infinity;
    for (const item of filtered) {
      if (!item.value) continue;
      const dist = levenshtein(lower, item.value.toLowerCase());
      if (dist > 0 && dist <= threshold && dist < bestDist) {
        bestDist = dist;
        bestMatch = item;
      }
    }
    if (bestMatch) {
      // TASK-1334 — offer the TRIMMED value: Apply assigns this straight into
      // the input, so an untrimmed suggestion propagates stored whitespace
      // into the next record.
      const _suggestion = String(bestMatch.value).trim();
      showWarning(fieldId, {
        type: 'similar',
        icon: 'info',
        message: `Did you mean "${_suggestion}"?`,
        suggestion: _suggestion
      });
      return;
    }
  }

  // 3. Format issues
  if (hasFormatIssues(val, options)) {
    const fixed = cleanFormat(val, options);
    if (fixed !== val) {
      showWarning(fieldId, {
        type: 'format',
        icon: 'auto_fix_high',
        message: `Suggestion: "${fixed}"`,
        suggestion: fixed
      });
    }
  }
}

// SECURITY (M9, 2026-06-25): warning.message embeds registry values (person /
// vehicle / customer names entered by other users) and is injected via innerHTML
// below — escape it so a stored name like `<img src=x onerror=…>` can't execute in
// another user's session when the duplicate/similar warning renders. Messages are
// plain-text sentences (no intended markup), so full-string escaping is safe.
function _saEsc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (m) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[m]));
}

// Show a warning below the input's .form-group parent
function showWarning(fieldId, warning) {
  clearWarning(fieldId);
  const input = document.getElementById(fieldId);
  if (!input) return;

  const parent = input.closest('.form-group') || input.closest('.tb-field') || input.parentElement;
  if (!parent) return;

  const div = document.createElement('div');
  div.className = `smart-warning smart-warning--${warning.type}`;
  div.setAttribute('data-sa-field', fieldId);
  // HOTFIX 2026-11-04 (J-1355 / J-1361) — record the value this suggestion was
  // computed FROM. The modals are reused DOM: a warning raised on job A's notes
  // survived into job B, and Auto-fix would have written A's notes into B.
  // handleWarningClick refuses to apply unless the field still holds this value.
  div.setAttribute('data-sa-from', String(input.value == null ? '' : input.value));

  // type="button" on every button here and in showClassWarning: these render
  // INSIDE <form> elements (#form-truck onsubmit=saveTruck), and a bare <button>
  // is a submit button — clicking Auto-fix would save the half-edited form.
  let actionHtml = '';
  if (warning.suggestion && warning.type === 'similar') {
    actionHtml = `<button type="button" class="smart-warning-action" data-sa-apply="${_saEsc(fieldId)}" data-sa-value="${_saEsc(warning.suggestion)}">Apply</button>`;
  } else if (warning.suggestion && warning.type === 'format') {
    actionHtml = `<button type="button" class="smart-warning-action" data-sa-apply="${_saEsc(fieldId)}" data-sa-value="${_saEsc(warning.suggestion)}">Auto-fix</button>`;
  }

  div.innerHTML = `
    <span class="material-icons smart-warning-icon">${_saEsc(warning.icon)}</span>
    <span class="smart-warning-text">${_saEsc(warning.message)}</span>
    ${actionHtml}
    <button type="button" class="smart-warning-dismiss" data-sa-dismiss="${_saEsc(fieldId)}" title="Dismiss">&times;</button>
  `;

  parent.appendChild(div);
}

// HOTFIX 2026-11-04 — remove every Smart Assist warning (id- and class-based)
// inside a container. Called when a reused modal/form is (re)populated for a
// different record, and on every form 'reset' event. Only SA-owned banners are
// touched (data-sa-field / data-sa-class) — poField.js and #job-duplicate-warning
// reuse the .smart-warning class and must survive.
function clearAll(container) {
  const root = container || document;
  if (!root.querySelectorAll) return;
  root.querySelectorAll('.smart-warning[data-sa-field], .smart-warning[data-sa-class]')
    .forEach((el) => el.remove());
}

// Clear warning for a field
function clearWarning(fieldId) {
  const existing = document.querySelector(`.smart-warning[data-sa-field="${fieldId}"]`);
  if (existing) existing.remove();
}

// Handle action clicks (Apply / Auto-fix / Dismiss) via delegation
function handleWarningClick(e) {
  const applyBtn = e.target.closest('[data-sa-apply]');
  if (applyBtn) {
    const fid = applyBtn.getAttribute('data-sa-apply');
    const val = applyBtn.getAttribute('data-sa-value');
    const input = document.getElementById(fid);
    // HOTFIX 2026-11-04 — stale-suggestion guard. A suggestion is only valid for
    // the exact value it was computed from (data-sa-from, set in showWarning).
    // If the field now holds anything else (form re-populated for another
    // record, user kept typing), never write: drop the banner and re-check the
    // CURRENT value instead. A warning with no recorded source is treated as stale.
    const warnEl = applyBtn.closest('.smart-warning');
    const from = warnEl ? warnEl.getAttribute('data-sa-from') : null;
    if (!input || from === null || input.value !== from) {
      if (warnEl) warnEl.remove();
      clearWarning(fid);
      if (input && window.hasAiPack && window.hasAiPack() && isEnabled()) runCheck(fid);
      return;
    }
    if (input) {
      input.value = val;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      // Also trigger onchange for textareas bound with onchange
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    // Re-check after applying
    setTimeout(() => runCheck(fid), 50);
    return;
  }

  const dismissBtn = e.target.closest('[data-sa-dismiss]');
  if (dismissBtn) {
    const fid = dismissBtn.getAttribute('data-sa-dismiss');
    clearWarning(fid);
  }
}

// Show/hide the "Smart Assist active" indicator on focus
function showFocusIndicator(input) {
  const parent = input.closest('.form-group') || input.closest('.tb-field') || input.parentElement;
  if (!parent) return;
  if (parent.querySelector('.sa-focus-badge')) return;
  const badge = document.createElement('div');
  badge.className = 'sa-focus-badge';
  badge.innerHTML = '<span class="material-icons" style="font-size:12px;">auto_fix_high</span> Smart Assist';
  parent.insertBefore(badge, input);
}

function hideFocusIndicator(input) {
  const parent = input.closest('.form-group') || input.closest('.tb-field') || input.parentElement;
  if (!parent) return;
  const badge = parent.querySelector('.sa-focus-badge');
  if (badge) badge.remove();
}

// Initialize Smart Assistant — single event listener via delegation
export function initSmartAssistant() {
  // focusin — show "Smart Assist" badge
  document.addEventListener('focusin', (e) => {
    if (!window.hasAiPack || !window.hasAiPack()) return;
    if (!isEnabled()) return;
    const input = e.target;
    if (!input) return;
    const isRegistered = (input.id && SA_REGISTRY[input.id]) ||
      SA_CLASS_REGISTRY.some(cls => input.matches?.(cls.selector));
    if (isRegistered) showFocusIndicator(input);
  }, true);

  // focusout listener on document for all registered inputs
  document.addEventListener('focusout', (e) => {
    if (!window.hasAiPack || !window.hasAiPack()) return;
    if (!isEnabled()) return;
    const input = e.target;
    if (!input) return;

    // Hide focus badge
    hideFocusIndicator(input);

    // Check by ID first
    if (input.id && SA_REGISTRY[input.id]) {
      runCheck(input.id);
      return;
    }

    // Check class-based dynamic fields
    for (const cls of SA_CLASS_REGISTRY) {
      if (input.matches(cls.selector)) {
        runClassCheck(input, cls);
        return;
      }
    }
  }, true);

  // Click delegation for action buttons inside warnings
  document.addEventListener('click', handleWarningClick);

  // HOTFIX 2026-11-04 — form.reset() is how most Add paths repopulate a reused
  // modal; drop that form's stale Smart Assist banners with it.
  document.addEventListener('reset', (e) => {
    if (e.target && e.target.querySelectorAll) clearAll(e.target);
  }, true);

  // Expose API on window
  window.SmartAssistant = {
    register(fieldId, getter, label, options = {}) {
      SA_REGISTRY[fieldId] = { getter, label, options };
    },
    unregister(fieldId) {
      delete SA_REGISTRY[fieldId];
      clearWarning(fieldId);
    },
    check(fieldId) {
      if (!window.hasAiPack || !window.hasAiPack()) return;
      if (!isEnabled()) return;
      runCheck(fieldId);
    },
    clearAll(container) {
      clearAll(container);
    },
    registerClass(selector, options = {}) {
      // Remove existing if same selector
      const idx = SA_CLASS_REGISTRY.findIndex(c => c.selector === selector);
      if (idx >= 0) SA_CLASS_REGISTRY.splice(idx, 1);
      SA_CLASS_REGISTRY.push({ selector, options });
    },
    unregisterClass(selector) {
      const idx = SA_CLASS_REGISTRY.findIndex(c => c.selector === selector);
      if (idx >= 0) SA_CLASS_REGISTRY.splice(idx, 1);
    }
  };
}

// Class-based dynamic field registry (for innerHTML-generated inputs)
const SA_CLASS_REGISTRY = [];

// Run check for class-based dynamic fields
function runClassCheck(input, cls) {
  const val = input.value;
  if (!val || !val.trim()) return;

  const options = cls.options || {};

  // Clear any existing warning on this element
  const parent = input.closest('.form-group') || input.closest('.tb-field') || input.parentElement;
  if (parent) {
    const existing = parent.querySelector('.smart-warning');
    if (existing) existing.remove();
  }

  // Phone format
  if (options.phoneFormat) {
    const digits = val.trim().replace(/\D/g, '');
    if (digits.length >= 7) {
      const formatted = formatPhone(val.trim());
      if (formatted && formatted !== val.trim()) {
        showClassWarning(input, parent, {
          type: 'format',
          icon: 'auto_fix_high',
          message: `Suggestion: "${formatted}"`,
          suggestion: formatted
        });
      }
    }
    return;
  }

  // Email format
  if (options.emailFormat) {
    const error = validateEmail(val);
    if (error) {
      showClassWarning(input, parent, { type: 'format', icon: 'warning', message: error });
    }
    return;
  }

  // Identifier (CRM `.addr-value` addresses) — whitespace-only, never case.
  // cleanFormat('123 MAIN ST, WILLISTON ND 58801') === '123 Main St, Williston Nd 58801'.
  if (options.identifier) {
    const wsFix = identifierWhitespaceFix(val);
    if (wsFix) {
      showClassWarning(input, parent, {
        type: 'format',
        icon: 'auto_fix_high',
        message: `Suggestion: "${wsFix}"`,
        suggestion: wsFix
      });
    }
    return;
  }

  // Format-only
  if (hasFormatIssues(val, options)) {
    const fixed = cleanFormat(val, options);
    if (fixed !== val) {
      showClassWarning(input, parent, {
        type: 'format',
        icon: 'auto_fix_high',
        message: `Suggestion: "${fixed}"`,
        suggestion: fixed
      });
    }
  }
}

// Show warning for class-based fields (no ID needed)
function showClassWarning(input, parent, warning) {
  if (!parent) return;
  const div = document.createElement('div');
  div.className = `smart-warning smart-warning--${warning.type}`;
  div.setAttribute('data-sa-class', '1');
  // HOTFIX 2026-11-04 — same stale-suggestion guard as the id-based path.
  const fromVal = String(input.value == null ? '' : input.value);

  let actionHtml = '';
  if (warning.suggestion) {
    actionHtml = `<button type="button" class="smart-warning-action sa-class-apply" data-sa-value="${_saEsc(warning.suggestion)}">${warning.type === 'similar' ? 'Apply' : 'Auto-fix'}</button>`;
  }

  div.innerHTML = `
    <span class="material-icons smart-warning-icon">${_saEsc(warning.icon)}</span>
    <span class="smart-warning-text">${_saEsc(warning.message)}</span>
    ${actionHtml}
    <button type="button" class="smart-warning-dismiss sa-class-dismiss" title="Dismiss">&times;</button>
  `;

  // Wire up class-based apply/dismiss
  const applyBtn = div.querySelector('.sa-class-apply');
  if (applyBtn) {
    applyBtn.addEventListener('click', () => {
      if (input.isConnected === false || input.value !== fromVal) { div.remove(); return; }
      input.value = warning.suggestion;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      div.remove();
    });
  }
  const dismissBtn = div.querySelector('.sa-class-dismiss');
  if (dismissBtn) {
    dismissBtn.addEventListener('click', () => div.remove());
  }

  parent.appendChild(div);
}
